import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import {
  getUserProfile,
  getStripePayments,
} from "../../services/firestoreService";
import {
  MedicationOrderStatus,
  determineMedicationOrderStatus,
} from "../../utils/medicationOrderUtils";
import { MedicationOrderStatusParams, StripePayment } from "../../typings";
import { useLatestPrescriptionRealtime } from "./useLatestPrescriptionRealtime";
import useProductOrderStatus from "./useProductOrderStatus";
import { getTransactions } from "../../services/paymentsService";
import { TransactionData } from "../../typings/paymentsServiceTypes";
import { useLatestConsultRealtime } from "./useLatestConsultRealtime";
import { ConsultType } from "../../typings/enums";

interface UseMedicationOrderStatus {
  status: MedicationOrderStatus;
  orderNumber: string;
  isLoading: boolean;
}

type MedicationOrderPayment = TransactionData | StripePayment | null;

export const useMedicationOrderStatus = (): UseMedicationOrderStatus => {
  const { currentUser } = useAuth();

  const { productData } = useProductOrderStatus(currentUser?.uid ?? "");
  const { isLoading: userProfileLoading, data: userProfile } = useQuery({
    queryKey: ["userProfile", currentUser?.uid],
    queryFn: () => getUserProfile(currentUser!),
    enabled: !!currentUser,
  });

  const { data: medicationOrderPayment, isLoading: medicationOrderLoading } =
    useQuery<MedicationOrderPayment>({
      queryKey: ["medicationOrderPayment", currentUser?.uid],
      queryFn: async () => {
        try {
          if (userProfile?.paymentsService) {
            const transactions = await getTransactions(currentUser, true);
            if (!transactions || transactions.length === 0) {
              return null;
            }
            return {
              ...transactions[0],
              status: transactions[0].intent_outcome,
            };
          } else {
            const payments = await getStripePayments(currentUser?.uid ?? "");
            return (
              payments?.find(
                (payment) => payment.productCategory === "medication"
              ) ?? null
            );
          }
        } catch (error) {
          console.error("Error fetching medication order payment:", error);
          throw error;
        }
      },
      enabled:
        !!currentUser &&
        !productData?.description &&
        !userProfileLoading &&
        !!userProfile, // Only run this query if productData is available which indicates it's a bundle,
    });

  // Fetch both initial_async and async_messaging consults to properly handle
  // the missed_sla flow where an async_messaging consult is created after rejection
  const { data: initialAsyncConsult, isLoading: initialAsyncLoading } =
    useLatestConsultRealtime(
      [ConsultType.initial_async],
      false,
      true // Include rejected missed_sla so status is AsyncOnHold and we show "Chat with Provider"
    );

  const { data: asyncMessagingConsult, isLoading: asyncMessagingLoading } =
    useLatestConsultRealtime([ConsultType.async_messaging], false, false);

  const prescription = useLatestPrescriptionRealtime();

  // Prioritize async_messaging over initial_async (async_messaging is more recent)
  // determineMedicationOrderStatus will check consult status first, then flow through
  // to prescription logic if consult is in Completed/WaitingToSchedule state
  const consult = asyncMessagingConsult ?? initialAsyncConsult;

  const medicationOrderStatus = determineMedicationOrderStatus({
    medicationOrderPayment,
    prescription,
    bundled: !!productData?.description,
    consult,
    pharmacyProvider: productData?.pharmacy_provider,
  } as MedicationOrderStatusParams);

  return {
    status: medicationOrderStatus ?? MedicationOrderStatus.Pending,
    orderNumber:
      prescription?.externalOrderId || prescription?.prescriptionId || "",
    isLoading:
      userProfileLoading ||
      medicationOrderLoading ||
      initialAsyncLoading ||
      asyncMessagingLoading,
  };
};
